// Learning tracks data aligned with central types
import { LearningTrack, LearningLevel, TrackType } from '@/types/learningPathTypes';

export const ALL_LEARNING_TRACKS: LearningTrack[] = [
  {
    id: 'fullstack-developer',
    name: 'Full Stack Developer',
    description: 'Complete path to become a full stack developer',
    type: 'fullstack',
    estimatedDuration: '12 months',
    icon: '🌐',
    color: '#3B82F6',
    levels: ['beginner', 'intermediate', 'advanced'] as LearningLevel[],
    pathways: [
      {
        id: 'frontend-path',
        name: 'Frontend Development',
        description: 'Master modern frontend development with React',
        level: 'beginner',
        estimatedDuration: '6 months',
        courseIds: ['javascript-fundamentals', 'react-fundamentals', 'js-intermediate', 'advanced-react'],
        prerequisites: [],
        skills: ['React', 'JavaScript', 'TypeScript', 'HTML/CSS'],
        outcomes: ['Build responsive UIs', 'Understand React fundamentals', 'Master TypeScript'],
        capstoneProject: {
          title: 'Portfolio Website',
          description: 'Build and deploy a personal portfolio website',
          estimatedTime: '2 weeks',
          technologies: ['React', 'Vite', 'Tailwind']
        }
      },
      {
        id: 'backend-path',
        name: 'Backend Development',
        description: 'Build scalable server-side applications',
        level: 'intermediate',
        estimatedDuration: '4 months',
        courseIds: ['nodejs-fundamentals', 'enterprise-api-development', 'sql-fundamentals', 'advanced-sql-analytics'],
        prerequisites: ['frontend-path'],
        skills: ['Node.js', 'Express', 'SQL', 'APIs'],
        outcomes: ['Build REST APIs', 'Work with databases', 'Deploy backend services']
      },
      {
        id: 'fullstack-path',
        name: 'Full Stack Integration',
        description: 'Combine frontend and backend skills',
        level: 'advanced',
        estimatedDuration: '2 months',
        courseIds: ['cloud-native-devops', 'microservices-serverless', 'genai-rag-supabase'],
        prerequisites: ['backend-path'],
        skills: ['Full Stack Architecture', 'Microservices', 'Deployment'],
        outcomes: ['Build complete applications', 'Deploy to production', 'Implement authentication'],
        capstoneProject: {
          title: 'Full Stack Application',
          description: 'Build a complete web application with authentication and database',
          estimatedTime: '4 weeks',
          technologies: ['React', 'Node.js', 'PostgreSQL', 'Supabase']
        }
      }
    ]
  },
  {
    id: 'data-scientist',
    name: 'Data Scientist',
    description: 'Master data science and machine learning',
    type: 'datascience',
    estimatedDuration: '10 months',
    icon: '📊',
    color: '#10B981',
    levels: ['beginner', 'intermediate', 'advanced'] as LearningLevel[],
    pathways: [
      {
        id: 'data-foundations-path',
        name: 'Data Foundations',
        description: 'Learn Python and data manipulation basics',
        level: 'beginner',
        estimatedDuration: '3 months',
        courseIds: ['python-fundamentals', 'python-intermediate', 'sql-fundamentals', 'data-science-fundamentals'],
        skills: ['Python', 'SQL', 'Data Manipulation', 'Statistics'],
        outcomes: ['Work with data in Python', 'Query databases', 'Understand statistics']
      },
      {
        id: 'ml-path',
        name: 'Machine Learning',
        description: 'Learn ML fundamentals and model building',
        level: 'intermediate',
        estimatedDuration: '5 months',
        courseIds: ['ai-fundamentals', 'machine-learning-intro', 'ml-intermediate', 'datascience-intermediate'],
        prerequisites: ['data-foundations-path'],
        skills: ['Machine Learning', 'Python', 'Statistics', 'Model Evaluation'],
        outcomes: ['Train ML models', 'Evaluate and deploy models', 'Feature engineering']
      },
      {
        id: 'advanced-ds-path',
        name: 'Advanced Data Science',
        description: 'Master advanced ML and MLOps',
        level: 'advanced',
        estimatedDuration: '2 months',
        courseIds: ['ml-advanced', 'datascience-advanced', 'mlops-deployment', 'machine-learning-production'],
        prerequisites: ['ml-path'],
        skills: ['Deep Learning', 'MLOps', 'Production ML', 'Advanced Analytics'],
        outcomes: ['Build production ML systems', 'Implement MLOps pipelines', 'Lead data projects'],
        capstoneProject: {
          title: 'ML Production Pipeline',
          description: 'Build an end-to-end ML pipeline with deployment',
          estimatedTime: '4 weeks',
          technologies: ['Python', 'Scikit-learn', 'Docker', 'MLflow']
        }
      }
    ]
  },
  {
    id: 'cloud-computing',
    name: 'Cloud Computing Mastery',
    description: 'Learn AWS, Azure, and GCP platforms',
    type: 'cloud',
    estimatedDuration: '7 months',
    icon: '☁️',
    color: '#6366F1',
    levels: ['beginner', 'intermediate', 'advanced'] as LearningLevel[],
    pathways: [
      {
        id: 'cloud-fundamentals-path',
        name: 'Cloud Fundamentals',
        description: 'Learn cloud basics and core services',
        level: 'beginner',
        estimatedDuration: '2-3 months',
        courseIds: ['aws-fundamentals', 'azure-fundamentals', 'edge-computing-fundamentals'],
        skills: ['AWS', 'Azure', 'Cloud Architecture', 'Networking'],
        outcomes: ['Understand cloud services', 'Deploy basic applications', 'Prepare for certifications']
      },
      {
        id: 'cloud-associate-path',
        name: 'Cloud Associate',
        description: 'Prepare for associate-level certifications',
        level: 'intermediate',
        estimatedDuration: '3 months',
        courseIds: ['aws-associate', 'aws-lambda-development', 'serverless-edge-functions'],
        prerequisites: ['cloud-fundamentals-path'],
        skills: ['Solutions Architecture', 'Serverless', 'Lambda', 'API Gateway'],
        outcomes: ['Design cloud solutions', 'Build serverless apps', 'Pass associate exams']
      },
      {
        id: 'cloud-advanced-path',
        name: 'Cloud Architect',
        description: 'Advanced cloud architecture and DevOps',
        level: 'advanced',
        estimatedDuration: '2 months',
        courseIds: ['aws-advanced', 'azure-enterprise-architecture', 'microservices-serverless'],
        prerequisites: ['cloud-associate-path'],
        skills: ['Enterprise Architecture', 'Multi-Cloud', 'Security', 'Cost Optimization'],
        outcomes: ['Design enterprise solutions', 'Implement security', 'Optimize costs'],
        capstoneProject: {
          title: 'Enterprise Cloud Architecture',
          description: 'Design and implement a multi-region cloud architecture',
          estimatedTime: '3 weeks',
          technologies: ['AWS', 'Terraform', 'Kubernetes', 'CI/CD']
        }
      }
    ]
  },
  {
    id: 'ai-engineer',
    name: 'AI Engineer',
    description: 'Master AI and deep learning technologies',
    type: 'ai',
    estimatedDuration: '12 months',
    icon: '🤖',
    color: '#F59E0B',
    levels: ['beginner', 'intermediate', 'advanced'] as LearningLevel[],
    pathways: [
      {
        id: 'ai-basics-path',
        name: 'AI Fundamentals',
        description: 'Core AI and ML concepts',
        level: 'beginner',
        estimatedDuration: '3 months',
        courseIds: ['ai-fundamentals', 'machine-learning-intro', 'python-intermediate'],
        skills: ['AI Concepts', 'Machine Learning', 'Neural Networks'],
        outcomes: ['Understand AI fundamentals', 'Implement basic ML', 'Use AI libraries']
      },
      {
        id: 'ml-engineering-path',
        name: 'ML Engineering',
        description: 'Build production ML systems',
        level: 'intermediate',
        estimatedDuration: '4 months',
        courseIds: ['ml-intermediate', 'data-pipeline-mlops', 'mlops-deployment', 'computer-vision-ai'],
        prerequisites: ['ai-basics-path'],
        skills: ['ML Engineering', 'MLOps', 'Computer Vision', 'Data Pipelines'],
        outcomes: ['Build ML pipelines', 'Deploy models', 'Monitor performance']
      },
      {
        id: 'advanced-ai-path',
        name: 'Advanced AI',
        description: 'Cutting-edge AI technologies',
        level: 'advanced',
        estimatedDuration: '5 months',
        courseIds: ['ml-advanced', 'reinforcement-learning-fundamentals', 'genai-rag-supabase', 'autonomous-systems'],
        prerequisites: ['ml-engineering-path'],
        skills: ['Deep Learning', 'Reinforcement Learning', 'Generative AI', 'Autonomous Systems'],
        outcomes: ['Build advanced AI systems', 'Work with LLMs', 'Create autonomous agents'],
        capstoneProject: {
          title: 'AI Application Suite',
          description: 'Build AI applications with computer vision, NLP, and generative AI',
          estimatedTime: '6 weeks',
          technologies: ['PyTorch', 'OpenAI', 'LangChain', 'Hugging Face']
        }
      }
    ]
  }
];

export const getTrackById = (id: string) => ALL_LEARNING_TRACKS.find(t => t.id === id);
export const getPathwayById = (trackId: string, pathwayId: string) => {
  const track = getTrackById(trackId);
  return track?.pathways.find(p => p.id === pathwayId);
};
